import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import { observer } from "mobx-react-lite";
import { useStore } from "@/models";
import { getRecommendPairs } from "../MarketList/recommendPairs";
import { parseSymbolParts } from "./utils";
import { IconFavStar } from "./icons";
import styles from "./recommendPc.module.less";

const { useTranslation } = Hooks;
const { showRate } = Util;

const PC_RECOMMEND_LIMIT = 8;

function CoinIcon({ symbol, fund }) {
  const { base } = parseSymbolParts(symbol);
  const rawLogo = fund?.currencyMap?.[base]?.logo;
  if (rawLogo) {
    return <img src={rawLogo} alt="" className={styles.coinIcon} />;
  }
  return (
    <span className={styles.coinFallback}>{(base || "?").slice(0, 1).toUpperCase()}</span>
  );
}

function RecommendPc({
  resData,
  wsData,
  activeFiatCodes,
  activeFiatCode,
  subTab = "",
}) {
  const t = useTranslation();
  const { market, user, fund } = useStore();
  const [pendingSymbol, setPendingSymbol] = useState("");
  const [addingAll, setAddingAll] = useState(false);

  const recommendPairs = useMemo(
    () =>
      getRecommendPairs({
        marketConfig: market.marketConfig,
        resData,
        wsData,
        activeFiatCodes,
        activeFiatCode,
        subTab,
        limit: PC_RECOMMEND_LIMIT,
      }),
    [market.marketConfig, resData, wsData, activeFiatCodes, activeFiatCode, subTab]
  );

  const userMarket = Array.isArray(user.userMarket) ? user.userMarket : [];

  const isAdded = (symbol) => userMarket.includes(symbol);

  const allAdded =
    recommendPairs.length > 0 && recommendPairs.every((item) => isAdded(item.s));

  const toggleFavorite = async (symbol) => {
    if (pendingSymbol || addingAll) return;
    setPendingSymbol(symbol);
    try {
      if (isAdded(symbol)) {
        await user.removeUserMarket(symbol);
      } else {
        await user.addUserMarket(symbol);
      }
    } catch {
      /* ignore */
    } finally {
      setPendingSymbol("");
    }
  };

  const onAddAll = async () => {
    if (addingAll || pendingSymbol) return;
    const toAdd = recommendPairs.map((item) => item.s).filter((sym) => !isAdded(sym));
    if (!toAdd.length) return;
    setAddingAll(true);
    try {
      const merged = Array.from(new Set([...userMarket, ...toAdd]));
      await user.setUserMarket(merged);
    } catch {
      /* ignore */
    } finally {
      setAddingAll(false);
    }
  };

  const formatChange = (item) => {
    const v = Number(item.cr ?? item.r ?? 0);
    return showRate(v * 100);
  };

  if (!recommendPairs.length) {
    return null;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <IconFavStar className={styles.headStar} active />
        <span className={styles.headTitle}>
          {t("market.pcRecommendTitle") || "Popular markets"}
        </span>
        <span className={styles.headHint}>
          {t("market.pcRecommendHint") || "— click to add to favorites"}
        </span>
      </div>

      <div className={styles.grid}>
        {recommendPairs.map((item) => {
          const { base, quote } = parseSymbolParts(item.s);
          const added = isAdded(item.s);
          const change = Number(item.cr ?? item.r ?? 0);
          const isUp = change >= 0;
          const busy = pendingSymbol === item.s || addingAll;

          return (
            <button
              key={`pc-rec-${item.s}`}
              type="button"
              className={classNames(styles.card, added && styles.added)}
              disabled={busy}
              onClick={() => toggleFavorite(item.s)}
            >
              <CoinIcon symbol={item.s} fund={fund} />
              <div className={styles.meta}>
                <div className={styles.symbolLine}>
                  {base.toUpperCase()}
                  <span className={styles.quote}>/{quote.toUpperCase()}</span>
                </div>
                <div className={classNames(styles.change, isUp ? styles.up : styles.down)}>
                  {formatChange(item)}
                </div>
              </div>
              <span className={classNames(styles.badge, added && styles.badgeAdded)}>
                {added ? "✓" : "+"}
              </span>
            </button>
          );
        })}
      </div>

      {!allAdded && (
        <button
          type="button"
          className={styles.addAllBtn}
          disabled={addingAll || !!pendingSymbol}
          onClick={onAddAll}
        >
          <IconFavStar className={styles.addAllStar} active={false} />
          {t("market.pcRecommendAddAll") || "Add all popular markets"}
        </button>
      )}
    </div>
  );
}

export default observer(RecommendPc);
